import { useEffect, useRef } from "react";
import "./BgFx.css";

/**
 * Animated background: grid + mouse-following spot + canvas particles
 * with constellation lines + drifting glow blobs.
 * Adapted from the Terminal-EX panel design.
 */
export default function BgFx() {
  const rootRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const fx = rootRef.current;
    const canvas = canvasRef.current;
    if (!fx || !canvas) return;

    let tx = window.innerWidth * 0.7;
    let ty = window.innerHeight * 0.12;
    let cx = tx;
    let cy = ty;

    const onMove = (e) => {
      tx = e.clientX;
      ty = e.clientY;
    };
    window.addEventListener("pointermove", onMove, { passive: true });

    const ctx = canvas.getContext("2d");
    let W = 0;
    let H = 0;
    let parts = [];

    const resize = () => {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
      const n = Math.min(72, Math.floor((W * H) / 26000));
      parts = Array.from({ length: n }, () => ({
        x: Math.random() * W,
        y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
        r: Math.random() * 1.5 + 0.5,
      }));
    };
    resize();
    window.addEventListener("resize", resize);

    let raf = 0;
    const frame = () => {
      cx += (tx - cx) * 0.12;
      cy += (ty - cy) * 0.12;
      fx.style.setProperty("--mx", `${cx}px`);
      fx.style.setProperty("--my", `${cy}px`);

      if (ctx) {
        ctx.clearRect(0, 0, W, H);
        for (const p of parts) {
          const dx = cx - p.x;
          const dy = cy - p.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 200 * 200) {
            const f = 1 - Math.sqrt(d2) / 200;
            p.vx += dx * 0.000016 * f * 60;
            p.vy += dy * 0.000016 * f * 60;
          }
          p.x += p.vx;
          p.y += p.vy;
          p.vx *= 0.965;
          p.vy *= 0.965;
          if (p.x < -20) p.x = W + 20;
          if (p.x > W + 20) p.x = -20;
          if (p.y < -20) p.y = H + 20;
          if (p.y > H + 20) p.y = -20;
        }
        // constellation lines
        for (let i = 0; i < parts.length; i++) {
          const a = parts[i];
          for (let j = i + 1; j < parts.length; j++) {
            const b = parts[j];
            const dx = a.x - b.x;
            const dy = a.y - b.y;
            const d = Math.hypot(dx, dy);
            if (d < 118) {
              const mx = (a.x + b.x) / 2 - cx;
              const my = (a.y + b.y) / 2 - cy;
              const near = Math.hypot(mx, my) < 220;
              ctx.strokeStyle = near
                ? `rgba(255,93,21,${(1 - d / 118) * 0.22})`
                : `rgba(255,255,255,${(1 - d / 118) * 0.05})`;
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(a.x, a.y);
              ctx.lineTo(b.x, b.y);
              ctx.stroke();
            }
          }
        }
        // dots
        for (const p of parts) {
          const dist = Math.hypot(cx - p.x, cy - p.y);
          const near = dist < 200;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
          ctx.fillStyle = near
            ? `rgba(255,93,21,${0.18 + (1 - dist / 200) * 0.5})`
            : "rgba(255,255,255,0.10)";
          ctx.fill();
        }
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div className="bg-fx" ref={rootRef} aria-hidden data-testid="bg-fx">
      <div className="bg-fx__grid" />
      <div className="bg-fx__grid-hot" />
      <canvas ref={canvasRef} className="bg-fx__dots" />
      <div className="bg-fx__spot" />
      <div className="bg-fx__b1" />
      <div className="bg-fx__b2" />
      <div className="bg-fx__b3" />
    </div>
  );
}
